// Truevision TGA decoder (types 1/2/3/9/10/11, 8/16/24/32 bit) producing
// RGBA pixels with the top row first.

export interface DecodedImage {
  width: number;
  height: number;
  data: Uint8Array; // RGBA
}

export function decodeTga(buf: Uint8Array): DecodedImage {
  const idLength = buf[0];
  const colorMapType = buf[1];
  const imageType = buf[2];
  const cmFirst = buf[3] | (buf[4] << 8);
  const cmLength = buf[5] | (buf[6] << 8);
  const cmDepth = buf[7];
  const width = buf[12] | (buf[13] << 8);
  const height = buf[14] | (buf[15] << 8);
  const depth = buf[16];
  const descriptor = buf[17];
  const rle = imageType >= 9;
  const baseType = rle ? imageType - 8 : imageType;
  if (![1, 2, 3].includes(baseType)) throw new Error(`TGA: unsupported image type ${imageType}`);
  if (!width || !height) throw new Error('TGA: empty image');

  let p = 18 + idLength;
  let palette: Uint8Array | null = null;
  if (colorMapType === 1) {
    const bpe = Math.ceil(cmDepth / 8);
    palette = new Uint8Array((cmFirst + cmLength) * 4);
    for (let i = 0; i < cmLength; i++) {
      readPixel(buf, p + i * bpe, cmDepth, palette, (cmFirst + i) * 4);
    }
    p += cmLength * bpe;
  }

  const bpp = Math.ceil(depth / 8);
  const pixels = width * height;
  const out = new Uint8Array(pixels * 4);
  const tmp = new Uint8Array(4);

  const decodeOne = (src: number, dst: number) => {
    if (baseType === 1) {
      const idx = bpp === 2 ? buf[src] | (buf[src + 1] << 8) : buf[src];
      out[dst] = palette![idx * 4];
      out[dst + 1] = palette![idx * 4 + 1];
      out[dst + 2] = palette![idx * 4 + 2];
      out[dst + 3] = palette![idx * 4 + 3];
    } else if (baseType === 3) {
      out[dst] = out[dst + 1] = out[dst + 2] = buf[src];
      out[dst + 3] = bpp === 2 ? buf[src + 1] : 255;
    } else {
      readPixel(buf, src, depth, tmp, 0);
      out[dst] = tmp[0];
      out[dst + 1] = tmp[1];
      out[dst + 2] = tmp[2];
      out[dst + 3] = tmp[3];
    }
  };

  if (!rle) {
    for (let i = 0; i < pixels; i++) decodeOne(p + i * bpp, i * 4);
  } else {
    let i = 0;
    while (i < pixels && p < buf.length) {
      const header = buf[p++];
      const count = (header & 0x7f) + 1;
      if (header & 0x80) {
        decodeOne(p, i * 4);
        for (let k = 1; k < count && i + k < pixels; k++) out.copyWithin((i + k) * 4, i * 4, i * 4 + 4);
        p += bpp;
      } else {
        for (let k = 0; k < count && i + k < pixels; k++) decodeOne(p + k * bpp, (i + k) * 4);
        p += count * bpp;
      }
      i += count;
    }
  }

  // TGA stores rows bottom-up unless descriptor bit 5 is set
  if (!(descriptor & 0x20)) flipRows(out, width, height);
  if (descriptor & 0x10) flipColumns(out, width, height);
  return { width, height, data: out };
}

function readPixel(buf: Uint8Array, src: number, depth: number, out: Uint8Array, dst: number): void {
  if (depth === 32) {
    out[dst] = buf[src + 2];
    out[dst + 1] = buf[src + 1];
    out[dst + 2] = buf[src];
    out[dst + 3] = buf[src + 3];
  } else if (depth === 24) {
    out[dst] = buf[src + 2];
    out[dst + 1] = buf[src + 1];
    out[dst + 2] = buf[src];
    out[dst + 3] = 255;
  } else if (depth === 15 || depth === 16) {
    const v = buf[src] | (buf[src + 1] << 8);
    out[dst] = ((v >> 10) & 31) * 8.226;
    out[dst + 1] = ((v >> 5) & 31) * 8.226;
    out[dst + 2] = (v & 31) * 8.226;
    out[dst + 3] = 255;
  } else {
    out[dst] = out[dst + 1] = out[dst + 2] = buf[src];
    out[dst + 3] = 255;
  }
}

function flipRows(data: Uint8Array, width: number, height: number): void {
  const row = width * 4;
  const tmp = new Uint8Array(row);
  for (let y = 0; y < height >> 1; y++) {
    const a = y * row;
    const b = (height - 1 - y) * row;
    tmp.set(data.subarray(a, a + row));
    data.copyWithin(a, b, b + row);
    data.set(tmp, b);
  }
}

function flipColumns(data: Uint8Array, width: number, height: number): void {
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width >> 1; x++) {
      const a = (y * width + x) * 4;
      const b = (y * width + width - 1 - x) * 4;
      for (let k = 0; k < 4; k++) {
        const t = data[a + k];
        data[a + k] = data[b + k];
        data[b + k] = t;
      }
    }
  }
}
